import React, {
  memo, useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import GoogleMapReact from 'google-map-react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { TravelTypes, googleApiKey } from '../../constants';
import Marker from './Marker';
import getHotelRateToDisplay from '../../helpers/getHotelRateToDisplay';
import getHotelPriceDisplayPreference from '../../helpers/selectors/getHotelPriceDisplayPreference';
import currencySymbol from '../../helpers/currencySymbol';
import getHotelRate from '../../helpers/selectors/getHotelRate';
import checkDoShowMarkupRates from '../../helpers/selectors/checkDoShowMarkupRates';
import HotelMapTooltip from './HotelMapTooltip';
import MapMarkerLegend from './MapMarkerLegend';
import { getTravelAgency } from '../../store/slices/user';
const defaultZoom = 15;

interface DisplayMapProps {
  onMarkerClick: (markerKey: string, pageNumber: number) => void;
  hoveredMarkerKey?: string;
  selectedMarkerKey?: string;
  markers: Array<any>; // Replace with the actual type of your markers
  tripId?: string;
}

const DisplayMap: React.FC<DisplayMapProps> = ({
  onMarkerClick, hoveredMarkerKey, markers,
  selectedMarkerKey, tripId,
}) => {
  const isTripSearch = !tripId;
  const doShowMarkupRate= useSelector(checkDoShowMarkupRates({mode:TravelTypes.hotel, tripId}));
  const travelAgency = useSelector(getTravelAgency);
  const mapRef = useRef(null);
  const pricePreference = useSelector(getHotelPriceDisplayPreference());
  let keyPropertyName = 'hotelKey';
  if (markers && markers.length && !markers[0][keyPropertyName] && markers[0].key) {
    keyPropertyName = 'key';
  }

 const doShowMapHighlighting = travelAgency?.settings?.doShowMapHighlighting;
 const isAnyRateCodeSelected = useSelector(
    (state: any) => state?.tripSearch?.searchObj?.hotel?.rateCodes?.length > 0
  );

  const [activeMarker, setActiveMarker] = useState<any>(null);
  const mapMarkerLegendRef = useRef<HTMLDivElement | null>(null);
 
  const handleMarkerMouseEnter = (marker: any) => {
    setActiveMarker(marker);
  };

  const handleMarkerMouseLeave = () => {
     setActiveMarker(null);
  };

  const setFitBounds = useCallback((map:any) => {
    if (!markers || markers.length < 2) { return; }
    const bounds = new (window as any).google.maps.LatLngBounds();
    markers.forEach((i) => {
      bounds.extend({
        lat: i.latitude,
        lng: i.longitude,
      });
    });
    map.fitBounds(bounds);
  }, [markers]);

  useEffect(() => {
    if (markers && markers.length && mapRef.current) {
      setFitBounds(mapRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markers.length]);

  const handleLoaded = ({ map } : any) => {
    setFitBounds(map);
    mapRef.current = map;

    if (mapMarkerLegendRef && doShowMapHighlighting) {
      map.controls[(window as any).google.maps.ControlPosition.LEFT_BOTTOM].push(mapMarkerLegendRef.current);
    }
  };

  if (!markers || !markers.length) {
    return <></>;
  }
  
  return (
    <div className={clsx('w-1/2 sticky top-0 h-screen flex-shrink-0 flex-grow-0 overflow-hidden', { 'w-full pt-0 border border-gray-300 shadow rounded-md': isTripSearch, 'px-2 py-5': !isTripSearch })}>
      <GoogleMapReact
        yesIWantToUseGoogleMapApiInternals
        bootstrapURLKeys={{ key: googleApiKey }}
        defaultZoom={defaultZoom}
        center={{
          lat: markers[0].latitude,
          lng: markers[0].longitude,
        }}
        onGoogleApiLoaded={handleLoaded}
      >
        {
          !!markers.length && markers.map((i) => (
            <Marker
              selected={selectedMarkerKey === i[keyPropertyName]}
              hovered={!!hoveredMarkerKey && hoveredMarkerKey === i[keyPropertyName]}
              key={i[keyPropertyName]}
              lat={i.latitude}
              lng={i.longitude}
              text={getHotelRateToDisplay(pricePreference, 
                getHotelRate({
                  doShowMarkupRate,
                  rate: {
                    averageNightlyRate: i.avgNightlyRate,
                    averageNightlyRateBeforeTax: i.avgNightlyRateBeforeTax,
                    currencyCode: i.currencyCode,
                    markupAverageNightlyRate: i.markupAvgNightlyRate,
                    markupAverageNightlyRateBeforeTax: i.markupAvgNightlyRateBeforeTax,
                    markupPrice: i.markupCost,
                    originalPrice: i.cost,
                  }
                }))}
              markerKey={i[keyPropertyName]}
              pageNumber={i.pageNumber}
              onClick={onMarkerClick}
              currency={currencySymbol(i.currencyCode)}
              onMouseEnter={() => handleMarkerMouseEnter(i)}
              onMouseLeave={handleMarkerMouseLeave}
              isAgentPreferredHotel={i.isAgentPreferredHotel}
              hasRateCodeMatch={i.hasRateCodeMatch}
              isAnyRateCodeSelected = {isAnyRateCodeSelected}
              doShowMapHighlighting = {doShowMapHighlighting}
          />
        ))}
        {activeMarker && <HotelMapTooltip marker={activeMarker} lat={activeMarker.latitude} lng={activeMarker.longitude} />}
      </GoogleMapReact>
      <div style={{ display: 'none' }}>
        <MapMarkerLegend
          ref={mapMarkerLegendRef}
          isPreferred
          isPartnerRateAvailable={isAnyRateCodeSelected}
          isNoPartnerRateAvailable={isAnyRateCodeSelected}
        />
      </div>
    </div>
  );
};

DisplayMap.propTypes = {
  onMarkerClick: PropTypes.func.isRequired,
  hoveredMarkerKey: PropTypes.string,
  selectedMarkerKey: PropTypes.string,
  markers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  tripId: PropTypes.string,
};

DisplayMap.defaultProps = {
  hoveredMarkerKey: undefined,
  selectedMarkerKey: undefined,
  tripId: undefined,
};
export default memo(DisplayMap);
